import React from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Shield, 
  Users, 
  Target, 
  Award, 
  TrendingUp,
  Gamepad2,
  Sparkles,
  Heart,
  Zap,
  Globe,
  Mail,
  Instagram,
  Youtube,
  ChevronRight,
  Star,
  CheckCircle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const About = () => {
  const values = [
    {
      icon: <Shield className="w-8 h-8 text-[#00fff5]" />,
      title: 'Integrity',
      desc: 'We maintain complete transparency in all tournaments and transactions.'
    },
    {
      icon: <Target className="w-8 h-8 text-[#00fff5]" />,
      title: 'Skill First',
      desc: 'We believe in rewarding true gaming skill, not luck or chance.'
    },
    {
      icon: <Heart className="w-8 h-8 text-[#00fff5]" />,
      title: 'Community',
      desc: 'Building India\'s strongest esports community, one player at a time.'
    },
    {
      icon: <Zap className="w-8 h-8 text-[#00fff5]" />,
      title: 'Innovation',
      desc: 'Constantly evolving to provide the best gaming experience.'
    }
  ];

  const milestones = [
    { year: '2024', event: 'TheArenaX founded with a vision for skill-based gaming' },
    { year: '2024', event: 'Launched BGMI & Free Fire tournament platform' },
    { year: '2025', event: 'Crossed 1,000 active players milestone' },
    { year: '2025', event: 'Processed ₹5 k+ in prize money' },
    { year: '2026', event: 'Introduced UPI withdrawals' },
    { year: '2026', event: 'Expanded to pan-India operations' },
  ];

  const team = [
    {
      name: 'SK Prince',
      role: 'Founder & CEO',
      desc: 'Esports enthusiast with 10+ years of gaming experience',
      icon: '👑'
    },
    {
      name: 'Sk Samrat',
      role: 'Head of Operations',
      desc: 'Former pro player, ensures fair play in all tournaments',
      icon: '🎮'
    },
    {
      name: 'Suroj Mondal',
      role: 'Community Manager',
      desc: 'Building and nurturing our gaming community',
      icon: '🤝'
    },
    {
      name: 'Sk Sahid',
      role: 'Technical Lead',
      desc: 'Ensures smooth platform experience for all users',
      icon: '⚡'
    }
  ];

  const stats = [
    { value: '2K+', label: 'Active Players', icon: Users },
    { value: '500+', label: 'Tournaments Hosted', icon: Trophy },
    { value: '₹5k+', label: 'Prize Money', icon: Award },
    { value: '24/7', label: 'Player Support', icon: Heart },
  ];

    const handleDownload = () => {
  // Create a temporary anchor element
  const link = document.createElement('a');
  
  // Path to your APK file (place it in public/apk/ folder)
  link.href = '/apk/TheArenaX-v2.0.0.apk';
  
  // Suggested filename for download
  link.download = 'TheArenaX-v2.0.0.apk';
  
  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section with Animated Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#00fff5] rounded-full opacity-5"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <Gamepad2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-rajdhani font-bold text-white mb-6">
              About <span className="text-[#00fff5]">TheArenaX</span>
            </h1>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              India's premier skill-based gaming platform where players compete in BGMI and Free Fire tournaments 
              to win real cash prizes. We're building a community of passionate gamers.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00E676] font-rajdhani font-bold">✓ Skill-Based Gaming</span>
              </div>
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold">⚡ 18+ Only</span>
              </div>
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#FFD700] font-rajdhani font-bold">🏆 UPI Withdrawals</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
              >
                <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-3 group-hover:bg-[#00fff520] transition">
                  <stat.icon className="w-8 h-8 text-[#00fff5]" />
                </div>
                <div className="text-3xl md:text-4xl font-rajdhani font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400 font-rajdhani">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-[#00fff520] border border-[#00fff5] rounded-full mb-6">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">OUR JOURNEY</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                Building India's <span className="text-[#00fff5]">Gaming Future</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-6 leading-relaxed">
                TheArenaX was born from a simple observation: Indian gamers deserve a platform that rewards 
                their skill with real value. We started in 2024 with a mission to create a transparent, 
                fair, and exciting esports ecosystem.
              </p>
              <p className="text-gray-400 font-rajdhani text-lg mb-8 leading-relaxed">
                Today, we're proud to be one of India's fastest-growing skill-based gaming platforms, 
                hosting thousands of tournaments and distributing millions in prize money to deserving 
                players across the country.
              </p>
              
              <div className="flex gap-4">
                <div className="bg-[#1E2338] p-4 rounded-xl border border-[#2A2F45]">
                  <CheckCircle className="w-6 h-6 text-[#00E676] mb-2" />
                  <span className="text-white font-rajdhani font-bold">Legal & Compliant</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] p-1 rounded-3xl">
                <div className="bg-[#0A0F1E] rounded-3xl p-8">
                  <div className="space-y-4">
                    {milestones.map((item, index) => (
                      <div key={index} className="flex items-start gap-4 group">
                        <div className="w-16 text-[#00fff5] font-rajdhani font-bold text-xl">{item.year}</div>
                        <div className="flex-1 bg-[#1E2338] p-4 rounded-xl border border-[#2A2F45] group-hover:border-[#00fff5] transition">
                          <p className="text-white font-rajdhani">{item.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Floating Sparkle */}
              <div className="absolute -top-4 -right-4 animate-pulse">
                <Sparkles className="w-8 h-8 text-[#FFD700]" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-4">
              <Heart className="w-8 h-8 text-[#00fff5]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              What We <span className="text-[#00fff5]">Stand For</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Our core values drive everything we do at TheArenaX
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
              >
                <div className="inline-block p-4 bg-[#00fff510] rounded-xl mb-4 group-hover:bg-[#00fff520] transition">
                  {value.icon}
                </div>
                <h3 className="text-xl font-rajdhani font-bold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 font-rajdhani">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] p-1 rounded-3xl">
                <div className="bg-[#0A0F1E] rounded-3xl p-8">
                  <div className="space-y-4">
                    {[
                      'Skill-based tournaments - 100% legal',
                      'Instant UPI withdrawals',
                      'Active anti-cheat system',
                      '24/7 customer support',
                      'Regular tournaments with big prizes',
                      'Transparent results & fair play',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#00E676] flex-shrink-0" />
                        <span className="text-white font-rajdhani">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-block px-4 py-2 bg-[#00fff520] border border-[#00fff5] rounded-full mb-6">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">WHY US</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                Why Players <span className="text-[#00fff5]">Choose Us</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-6 leading-relaxed">
                We've built TheArenaX with players at the heart of everything. From instant withdrawals 
                to fair matchmaking, every feature is designed to give you the best competitive gaming 
                experience in India.
              </p>
              <p className="text-gray-400 font-rajdhani text-lg mb-8 leading-relaxed">
                our advanced 
                anti-cheat system guarantees fair play for everyone.
              </p>
              
              <button className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold flex items-center gap-2 hover:opacity-90 transition group">
                Join The Arena
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-4">
              <Users className="w-8 h-8 text-[#00fff5]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Meet the <span className="text-[#00fff5]">Team</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Passionate gamers building the future of Indian esports
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
              >
                <div className="text-6xl mb-4 group-hover:scale-110 transition">{member.icon}</div>
                <h3 className="text-xl font-rajdhani font-bold text-white mb-1">{member.name}</h3>
                <p className="text-[#00fff5] font-rajdhani font-bold text-sm mb-3">{member.role}</p>
                <p className="text-gray-400 font-rajdhani text-sm">{member.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact & CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-3xl p-12 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
              Ready to <span className="text-[#00fff5]">Compete</span>?
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8">
              Join thousands of players already winning on TheArenaX
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button onClick={handleDownload} className="bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition">
                Download App
              </button>
              <button className="bg-[#1E2338] border border-[#2A2F45] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:bg-[#2A2F45] transition">
                View Tournaments
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#00fff5]" />
                <a href="mailto:support@lastzoneindia.com" className="text-white font-rajdhani hover:text-[#00fff5] transition">
                  support@lastzoneindia.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-[#00fff5]" />
                <span className="text-white font-rajdhani">www.thearenax.com</span>
              </div>
            </div>

            <div className="flex justify-center gap-4 mt-8">
              <a href="https://instagram.com/lastzoneindia" target="_blank" rel="noopener noreferrer" 
                 className="bg-[#1E2338] p-3 rounded-xl hover:bg-[#2A2F45] transition group">
                <Instagram className="w-6 h-6 text-gray-400 group-hover:text-[#00fff5]" />
              </a>
              <a href="https://youtube.com/@lastzoneindia" target="_blank" rel="noopener noreferrer"
                 className="bg-[#1E2338] p-3 rounded-xl hover:bg-[#2A2F45] transition group">
                <Youtube className="w-6 h-6 text-gray-400 group-hover:text-[#FF0000]" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;